import { Paper, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as monitoredProcessDataApi from '../../api/app/monitoredProcessDataApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { DataTable } from '../../components/core/DataTable/DataTable';
import { HeaderCell } from '../../components/core/DataTable/dataTable.types';
import { Loader } from '../../components/core/Loader/Loader';
import { useAuth } from '../../contexts/authWindows';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { MonitoredProcessData } from '../../types/app/monitoredProcessData.types';
import { UserRoles } from '../../types/core/auth.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  parentId: number;
}

export function MonitoredProcessDataList({ parentId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<MonitoredProcessData[]>(
    'monitoredProcessDatas',
    () => monitoredProcessDataApi.getMonitoredProcessDatasByDep(parentId),
  );
  const { mutate: mutateDeleteMonitoredProcessData } = useMutation(
    monitoredProcessDataApi.deleteMonitoredProcessData,
  );

  const headerCells: HeaderCell[] = [
    { title: 'Data type', key: 'dataType' },
    { title: 'OPC Client', key: 'opcClientInstanceName' },
    { title: 'Name', key: 'name' },
    { title: 'Influx value name', key: 'influxValueName' },
    { title: 'Node id', key: 'nodeId' },
    { title: 'Updated', key: 'updated', dataType: 'Date' },
  ];
  /*   const headerButtons = [
    <Fab
      key={0}
      color="primary"
      aria-label="add"
      size="medium"
      onClick={() => {
        navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_PROCESS_DATA_CREATE}`, {
          state: { parentId },
        });
      }}
    >
      <AddIcon />
    </Fab>,
  ]; */

  const handleOnCreate = () =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_PROCESS_DATA_CREATE}`, {
      state: { parentId },
    });

  const handleOnEdit = (id: number) =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_PROCESS_DATA_DETAIL}/${id}`);

  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteMonitoredProcessData(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.MONITORED_PROCESS_DATA_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['monitoredProcessDatas']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      {/* <PageHeader
        title={'Monitored process data'}
        buttons={isReadOnly ? null : headerButtons}
        headlineVariant={'h5'}
      /> */}
      <TableContainer component={Paper}>
        <DataTable
          headerCells={headerCells}
          items={data}
          onDeleteClick={handleOnDelete}
          onCreateClick={handleOnCreate}
          onEditClick={isReadOnly ? undefined : handleOnEdit}
          isReadOnly={isReadOnly}
        />
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
